import React, { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

// Custom Switch/Toggle Component matching the style
function Switch({ checked, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-accent/20 focus:ring-offset-2",
        checked ? "bg-primary" : "bg-gray-250"
      )}
    >
      <span
        className={cn(
          "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out",
          checked ? "translate-x-5" : "translate-x-0"
        )}
      />
    </button>
  );
}

/** GSTN UQC option keys under `uom` (reuses nos/h where applicable via nosNumbers/hrs). */
const UQC_OPTION_KEYS = [
  "none",
  "bag",
  "bal",
  "bdl",
  "bkl",
  "bou",
  "box",
  "btl",
  "bun",
  "can",
  "cbm",
  "ccm",
  "cms",
  "ctn",
  "doz",
  "drm",
  "fts",
  "gms",
  "grs",
  "gyd",
  "kgs",
  "klr",
  "kme",
  "ltr",
  "mtr",
  "mts",
  "nosNumbers",
  "pac",
  "pcs",
  "prs",
  "qtl",
  "rol",
  "set",
  "sqf",
  "sqm",
  "sqy",
  "tbs",
  "tgm",
  "thd",
  "ton",
  "yrd",
  "inc",
  "hrs",
  "day",
  "mon",
];

function getUqcOptions() {
  return UQC_OPTION_KEYS.map((key) => i18n.t(`uom.${key}`));
}

function getDefaultUqc() {
  return i18n.t("productMetrics.none");
}

const TAX_TYPES = ["GST", "VAT", "Service Tax", "Custom Tax"];

export function MasterValueModal({
  open,
  onOpenChange,
  type,
  editRow,
  onSave,
  rawData = [],
  onTriggerDeactivateDelete,
}) {
  const isEdit = Boolean(editRow);

  // Form states
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [description, setDescription] = useState("");
  const [uqc, setUqc] = useState(() => getDefaultUqc());
  const [taxType, setTaxType] = useState("");
  const [percentage, setPercentage] = useState("");
  const [iso, setIso] = useState("");
  const [base, setBase] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Form interaction validation states
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    symbol: false,
    taxType: false,
    percentage: false,
    iso: false,
  });

  const uqcOptions = useMemo(() => getUqcOptions(), []);

  // Dynamically load form states when editing changes
  useEffect(() => {
    if (open) {
      if (editRow) {
        setName(editRow.name || "");
        setSymbol(editRow.symbol || editRow.abbreviation || "");
        setDescription(editRow.description || "");
        const options = getUqcOptions();
        const none = getDefaultUqc();
        setUqc(
          editRow.uqc
            ? options.find((opt) => opt.startsWith(editRow.uqc)) || none
            : none
        );
        setTaxType(editRow.type || "");
        setPercentage(editRow.percentage !== undefined ? String(editRow.percentage) : "");
        setIso(editRow.iso || "");
        setBase(editRow.base === "Yes");
        setIsActive(
          editRow.isActive !== undefined
            ? editRow.isActive === true
            : editRow.status === "Active"
        );
      } else {
        // Reset states for create mode
        setName("");
        setSymbol("");
        setDescription("");
        setUqc(getDefaultUqc());
        setTaxType("");
        setPercentage("");
        setIso("");
        setBase(false);
        setIsActive(true);
      }
      setSubmitted(false);
      setSaving(false);
      setSaveError(null);
      setTouched({
        name: false,
        symbol: false,
        taxType: false,
        percentage: false,
        iso: false,
      });
    }
  }, [open, editRow]);

  const fieldTypeLabel = useMemo(() => {
    if (type === "uom") return i18n.t("productMetrics.unitOfMeasure");
    if (type === "tax") return i18n.t("productMetrics.taxRate");
    if (type === "currency") return i18n.t("productMetrics.currency");
    return type || "";
  }, [type]);

  const isLegacyUom = type === "uom";
  const isLegacyTax = type === "tax";
  const isLegacyCurrency = type === "currency";
  const isMetricCategory = Boolean(type) && !isLegacyUom && !isLegacyTax && !isLegacyCurrency;

  // Form validation checks
  const errors = useMemo(() => {
    const errs = {};

    // Common validations
    if (!name.trim()) {
      errs.name = i18n.t("productMetrics.thisFieldIsRequired");
    } else {
      const isNameDuplicate = rawData.some(
        (item) =>
          item.name?.toLowerCase().trim() === name.toLowerCase().trim() &&
          (!editRow ||
            (item.metricId != null
              ? item.metricId !== editRow.metricId
              : item.id !== editRow.id))
      );
      if (isNameDuplicate) {
        errs.name = `A ${fieldTypeLabel.toLowerCase()} with this name already exists.`;
      }
    }

    // UOM Fields
    if (isLegacyUom) {
      if (!symbol.trim()) {
        errs.symbol = i18n.t("productMetrics.thisFieldIsRequired");
      }
    }

    // Metric category fields (API-driven masters)
    if (isMetricCategory) {
      if (!symbol.trim()) {
        errs.symbol = i18n.t("productMetrics.thisFieldIsRequired");
      }
    }

    // Tax Fields
    if (isLegacyTax) {
      if (!taxType) {
        errs.taxType = i18n.t("productMetrics.thisFieldIsRequired");
      }
      if (!percentage.trim()) {
        errs.percentage = i18n.t("productMetrics.thisFieldIsRequired");
      } else {
        const val = parseFloat(percentage);
        if (isNaN(val) || val < 0 || val > 100) {
          errs.percentage = "Percentage must be between 0.00 and 100.00.";
        } else {
          const parts = String(percentage).split(".");
          if (parts[1] && parts[1].length > 2) {
            errs.percentage = "Percentage can have at most two decimal places.";
          }
        }
      }
    }

    // Currency Fields
    if (isLegacyCurrency) {
      if (!iso.trim()) {
        errs.iso = i18n.t("productMetrics.thisFieldIsRequired");
      } else {
        const isIsoDuplicate = rawData.some(
          (item) =>
            item.iso?.toLowerCase().trim() === iso.toLowerCase().trim() &&
            (!editRow || item.id !== editRow.id)
        );
        if (isIsoDuplicate) {
          errs.iso = "Currency code already exists.";
        }
      }
    }

    return errs;
  }, [
    name,
    symbol,
    taxType,
    percentage,
    iso,
    isLegacyUom,
    isLegacyTax,
    isLegacyCurrency,
    isMetricCategory,
    editRow,
    rawData,
    fieldTypeLabel,
  ]);

  // Error visibility helper
  const showError = (field) => errors[field] && (submitted || touched[field]);

  // Touch triggers
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSaveError(null);

    if (Object.keys(errors).length > 0 || saving) {
      return;
    }

    const payload = {
      id: editRow ? editRow.id : undefined,
      metricId: editRow?.metricId,
      name: name.trim(),
      status: isActive ? "Active" : "Inactive",
      isActive,
      category: type,
      ...(isLegacyUom && { symbol: symbol.trim(), uqc: uqc.split(" ")[0] }),
      ...(isLegacyTax && { type: taxType, percentage: parseFloat(percentage) }),
      ...(isLegacyCurrency && {
        iso: iso.toUpperCase().trim(),
        symbol: symbol.trim(),
        base: base ? "Yes" : "No",
      }),
      ...(isMetricCategory && {
        abbreviation: symbol.trim(),
        description: description.trim(),
      }),
    };

    setSaving(true);
    try {
      await onSave?.(payload);
      onOpenChange(false);
    } catch (err) {
      setSaveError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (saving && !nextOpen) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogPortal>
        {/* Backdrop overlay overlaying the configuration page */}
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[500px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "max-h-[90vh] flex flex-col justify-between font-sans focus:outline-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-2 border-b border-(--color-text-border)/70 shrink-0">
            <div>
              <DialogTitle className="text-lg font-bold text-foreground leading-tight">
                {isEdit ? i18n.t("productMetrics.editOption") : i18n.t("productMetrics.createOption")}
              </DialogTitle>
              {/* <p className="text-xs per-file-hint mt-1.5 font-medium">
                {isEdit
                  ? "Editing the master row only — issued documents keep their snapshot values."
                  : `This value becomes selectable everywhere ${fieldTypeLabel} is consumed.`}
              </p> */}
            </div>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          {/* Form Content (scrollable) */}
          <form
            onSubmit={handleSave}
            className="flex-1 overflow-y-auto px-6 py-5 space-y-6"
          >
            {/* 1. Common Field: Name */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                <Trans i18nKey="common.name" /> <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setTouched((prev) => ({ ...prev, name: true }));
                }}
                onBlur={() => handleBlur("name")}
                placeholder={
                  isLegacyUom
                    ? "e.g. Cubic Yard"
                    : isMetricCategory
                      ? "e.g. Australian Dollar"
                      : "e.g. GST 18%"
                }
                className={cn(
                  "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition",
                  showError("name") &&
                    "border-error focus:border-error focus:ring-error/10"
                )}
              />
              {showError("name") && (
                <p className="text-xs text-error font-medium mt-1">
                  {errors.name}
                </p>
              )}
              {/* {!showError("name") && (
                <p className="text-[11px] per-file-hint mt-1.5 leading-relaxed">
                  Must be unique within this field. Case and surrounding spaces are ignored when checking.
                </p>
              )} */}
            </div>

            {/* Metric category fields (API-driven) */}
            {isMetricCategory && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                    <Trans i18nKey="productMetrics.abbreviation" /> <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => {
                      setSymbol(e.target.value);
                      setTouched((prev) => ({ ...prev, symbol: true }));
                    }}
                    onBlur={() => handleBlur("symbol")}
                    placeholder={i18n.t("productMetrics.abbreviation")}
                    className={cn(
                      "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition",
                      showError("symbol") &&
                        "border-error focus:border-error focus:ring-error/10"
                    )}
                  />
                  {showError("symbol") && (
                    <p className="text-xs text-error font-medium mt-1">
                      {errors.symbol}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    <Trans i18nKey="roles.descriptionOptional" />
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Australian Dollar (A$)"
                    className="h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition"
                  />
                </div>
              </>
            )}

            {/* 2. Unit of Measure specific fields */}
            {isLegacyUom && (
              <>
                {/* Symbol */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                    <Trans i18nKey="productMetrics.symbol" /> <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => {
                      setSymbol(e.target.value);
                      setTouched((prev) => ({ ...prev, symbol: true }));
                    }}
                    onBlur={() => handleBlur("symbol")}
                    placeholder="e.g. MT"
                    className={cn(
                      "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition",
                      showError("symbol") &&
                        "border-error focus:border-error focus:ring-error/10"
                    )}
                  />
                  {showError("symbol") && (
                    <p className="text-xs text-error font-medium mt-1">
                      {errors.symbol}
                    </p>
                  )}
                  {/* {!showError("symbol") && (
                    <p className="text-[11px] per-file-hint mt-1.5 leading-relaxed">
                      Shown in dropdowns and on documents. Symbols do not have to be unique.
                    </p>
                  )} */}
                </div>

                {/* UQC Dropdown */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    <Trans i18nKey="productMetrics.uqcLabel" />
                  </label>
                  <Select value={uqc} onValueChange={setUqc}>
                    <SelectTrigger className="h-11 rounded-xl border border-(--color-text-border) bg-white text-sm text-left focus:ring-accent w-full transition">
                      <SelectValue placeholder={i18n.t("productMetrics.selectUqc")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {uqcOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* <p className="text-[11px] per-file-hint mt-1.5 leading-relaxed">
                    Optional. Only the 38 codes on the GSTN list are accepted — leave blank when no code maps to the unit.
                  </p> */}
                </div>
              </>
            )}

            {/* 3. Tax & Duties specific fields */}
            {isLegacyTax && (
              <>
                {/* Tax Type dropdown */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                    <Trans i18nKey="productMetrics.taxType" /> <span className="text-error">*</span>
                  </label>
                  <Select value={taxType} onValueChange={setTaxType}>
                    <SelectTrigger
                      className={cn(
                        "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm text-left focus:ring-accent w-full transition",
                        showError("taxType") && "border-error"
                      )}
                    >
                      <SelectValue placeholder={i18n.t("productMetrics.selectTaxType")} />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showError("taxType") && (
                    <p className="text-xs text-error font-medium mt-1">
                      {errors.taxType}
                    </p>
                  )}
                </div>

                {/* Percentage */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                    <Trans i18nKey="productMetrics.percentage" /> <span className="text-error">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={percentage}
                    onChange={(e) => {
                      setPercentage(e.target.value);
                      setTouched((prev) => ({ ...prev, percentage: true }));
                    }}
                    onBlur={() => handleBlur("percentage")}
                    placeholder="e.g. 18.00"
                    className={cn(
                      "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition",
                      showError("percentage") &&
                        "border-error focus:border-error focus:ring-error/10"
                    )}
                  />
                  {showError("percentage") && (
                    <p className="text-xs text-error font-medium mt-1">
                      {errors.percentage}
                    </p>
                  )}
                  {!showError("percentage") && (
                    <p className="text-[11px] per-file-hint mt-1.5 leading-relaxed">
                      Specify the tax percentage (between 0.00 and 100.00, maximum of two decimal places).
                    </p>
                  )}
                </div>
              </>
            )}

            {/* 4. Currency specific fields */}
            {isLegacyCurrency && (
              <>
                {/* ISO 4217 Code */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                    <Trans i18nKey="productMetrics.isoCode" /> <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={3}
                    value={iso}
                    onChange={(e) => {
                      setIso(e.target.value.toUpperCase());
                      setTouched((prev) => ({ ...prev, iso: true }));
                    }}
                    onBlur={() => handleBlur("iso")}
                    placeholder="e.g. INR"
                    className={cn(
                      "h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition",
                      showError("iso") &&
                        "border-error focus:border-error focus:ring-error/10"
                    )}
                  />
                  {showError("iso") && (
                    <p className="text-xs text-error font-medium mt-1">
                      {errors.iso}
                    </p>
                  )}
                  {!showError("iso") && (
                    <p className="text-[11px] per-file-hint mt-1.5 leading-relaxed">
                      Three-letter currency code defined by ISO 4217 (e.g. USD, EUR, INR).
                    </p>
                  )}
                </div>

                {/* Symbol (Optional) */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    Symbol
                  </label>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    placeholder="e.g. ₹"
                    className="h-11 rounded-xl border border-(--color-text-border) bg-white text-sm focus:ring-accent focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 w-full px-4 transition"
                  />
                </div>

                {/* Base Currency Toggle Switch Container */}
                <div className="rounded-xl border border-(--color-text-border)/65 bg-white p-4 flex items-start gap-3 transition-shadow hover:shadow-[0_4px_12px_-5px_rgba(30,51,131,0.08)]">
                  <div className="pt-0.5">
                    <Switch
                      checked={base}
                      onChange={setBase}
                      ariaLabel="Base Currency Toggle"
                    />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground block leading-tight">
                      Base Currency
                    </span>
                    <span className="text-xs per-file-hint mt-1.5 block leading-normal font-medium">
                      If enabled, this currency will be set as the base currency for conversion rates. Only one currency can be set as base.
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* 5. Active Status toggle Switch Container */}
            <div className="rounded-xl border border-(--color-text-border)/65 bg-white p-4 flex items-start gap-3 transition-shadow hover:shadow-[0_4px_12px_-5px_rgba(30,51,131,0.08)]">
              <div className="pt-0.5">
                <Switch
                  checked={isActive}
                  onChange={setIsActive}
                  ariaLabel="Active Status Toggle"
                />
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground block leading-tight">
                  Active
                </span>
                <span className="text-xs per-file-hint mt-1.5 block leading-normal font-medium">
                  Inactive values disappear from every dropdown immediately. 
                </span>
              </div>
            </div>

            {/* 6. Audit Information Section (Edit Mode Only) */}
            {/* {isEdit && (
              <div className="rounded-xl border border-(--color-text-border) bg-gray-50/50 p-4 space-y-3.5 mt-4">
                <h4 className="text-[10px] font-bold per-file-hint uppercase tracking-wider">
                  Audit Information
                </h4>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-[11px] font-medium per-file-hint">
                  <div>
                    <span className="block text-[10px] text-(--color-text-secondary)/80 uppercase tracking-wide">Created By</span>
                    <span className="font-semibold text-foreground block mt-0.5">John Smith</span>
                  </div>
                  <div>
                    <span className="block text-[10px] text-(--color-text-secondary)/80 uppercase tracking-wide">Created On</span>
                    <span className="font-semibold text-foreground block mt-0.5">12 Jul 2026, 10:45 AM</span>
                  </div>
                  <div className="pt-1.5 border-t border-(--color-text-border)/20 col-span-2"></div>
                  <div>
                    <span className="block text-[10px] text-(--color-text-secondary)/80 uppercase tracking-wide">Modified By</span>
                    <span className="font-semibold text-foreground block mt-0.5">Jane Doe</span>
                  </div>
                  <div>
                    <span className="block text-[10px] text-(--color-text-secondary)/80 uppercase tracking-wide">Modified On</span>
                    <span className="font-semibold text-foreground block mt-0.5">28 Jul 2026, 04:12 PM</span>
                  </div>
                </div>
              </div>
            )} */}
            
            {/* Invisible submit button to handle Enter key inside inputs */}
            <button type="submit" className="hidden" disabled={saving} />
          </form>

          {/* Dialog Footer (fixed/sticky) */}
          <div className="border-t border-(--color-text-border)/70 px-6 py-4 bg-white flex flex-col gap-3 shrink-0 rounded-b-2xl">
            {saveError && (
              <p className="text-xs text-error font-medium text-right">
                {saveError}
              </p>
            )}
            <div className="flex justify-end items-center gap-3">
            {/* {isEdit && (
              <button
                type="button"
                onClick={() => {
                  onTriggerDeactivateDelete(editRow);
                  onOpenChange(false);
                }}
                className="h-10 px-4 mr-auto rounded-xl border border-error/25 bg-white text-xs font-semibold text-error hover:bg-error/5 transition cursor-pointer"
              >
                {editRow.status === "Active" ? "Deactivate / Delete" : "Delete"}
              </button>
            )} */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="h-10 px-5 rounded-xl border border-(--color-text-border) per-file-hint font-semibold text-sm hover:bg-workspace/40 hover:text-primary transition shadow-xs cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trans i18nKey="formActions.cancel" />
            </button>
            <button
              type="button"
              disabled={hasErrors || saving}
              onClick={handleSave}
              className="h-10 px-6 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? i18n.t("inventory.saving") : <Trans i18nKey="formActions.save" />}
            </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
